/**
 * Store-layer types for agent chat state.
 */

import type {ImageMediaType} from '@wandb/weave/lib/WbAgent/promptParts';
import type {
  AgentQuestion,
  NetworkAccessRequest,
} from '@wandb/weave/lib/WbAgent/WbAgentClient';

export type MessageRole = 'user' | 'assistant' | 'system';

export type ReadonlyJSONValue =
  | null
  | string
  | number
  | boolean
  | {readonly [key: string]: ReadonlyJSONValue}
  | readonly ReadonlyJSONValue[];

export type RunOutcome = 'finished' | 'errored' | 'cancelled';

/**
 * ActiveThreadLoadFailure discriminates how the active thread's load failed.
 * 'not-found' means the load returned no turns; 'error' carries a message.
 */
export type ActiveThreadLoadFailure =
  | {kind: 'error'; message: string}
  | {kind: 'not-found'};

/**
 * INVARIANT — thread identity rotation:
 *
 * A new chat begins as a `drafts[<client-uuid>]` entry. On the first
 * `turn-created` event, the thread is promoted into `threads[<rootTurnId>]`,
 * a `promotionAliases[<client-uuid>] = <rootTurnId>` entry is recorded, and
 * `thread.id` / `thread.rootTurnId` are rewritten to `<rootTurnId>`.
 *
 * `activeThreadId` is the one identifier that does NOT rotate. It stays as
 * the creation-time client UUID for the lifetime of the session, because AUI
 * remounts the entire thread runtime on any `threadList.threadId` change
 * (verified against @assistant-ui/core v0.12.17), and remounting mid-stream
 * loses the in-flight assistant response.
 *
 * Consumers must therefore:
 *   - resolve thread reads via `findThread`, which transparently follows
 *     `promotionAliases`,
 *   - compare against the active thread via `isActiveThread`, NOT raw
 *     `=== state.activeThreadId`,
 *   - never key long-lived external state (pollers, AbortControllers,
 *     subscriptions, etc.) by `activeThreadId`; use the canonical
 *     `thread.id` or resolve through `findThread`.
 */
export interface AgentChatState {
  threads: Record<string, Thread>;
  drafts: Record<string, Thread>;
  promotionAliases: Record<string, string>;
  activeThreadId: string | null;
  isActiveThreadLoading: boolean;
  /**
   * activeThreadLoadFailure is the active thread's last load failure, or null
   * if the load succeeded, is in progress, or has not run.
   */
  activeThreadLoadFailure: ActiveThreadLoadFailure | null;
  /**
   * pendingRenames is the in-flight rename overlay; entries override
   * `Thread.title` for display.
   */
  pendingRenames: Record<string, {title: string}>;
  /**
   * pendingDeletes holds ids of threads currently being deleted; entries
   * hide the row from the chat history until the server confirms or the
   * mutation rolls back.
   */
  pendingDeletes: Set<string>;
  /**
   * deletedThreadIds holds ids whose server-side delete has been confirmed.
   * Sticky for the session so a late list refetch can't re-add a row.
   */
  deletedThreadIds: Set<string>;
}

type ThreadBuckets<T> = {
  threads: Record<string, T>;
  drafts: Record<string, T>;
  promotionAliases: Record<string, string>;
};

export function findThread<T>(
  state: ThreadBuckets<T>,
  id: string
): T | undefined {
  return (
    state.threads[id] ??
    state.drafts[id] ??
    state.threads[state.promotionAliases[id] ?? '']
  );
}

/**
 * isActiveThread tells whether `id` refers to the same thread as the store's
 * sticky `activeThreadId`, after resolving promotion aliases on both sides.
 *
 * Required because `activeThreadId` does not rotate on promotion (see the
 * invariant comment on `AgentChatState`): a raw `id === state.activeThreadId`
 * check returns false when one side is the original client UUID and the other
 * is the post-promotion `rootTurnId`, even though both name the same thread.
 *
 * Note: when `id` no longer resolves to a known thread (e.g. mid-deletion,
 * after the alias chain has been wiped), this returns false — callers that
 * care about the "was this active before the deletion?" state must capture
 * the result before mutating the buckets.
 */
export function isActiveThread<T>(
  state: ThreadBuckets<T> & {activeThreadId: string | null},
  id: string
): boolean {
  const active = state.activeThreadId;
  if (active == null) {
    return false;
  }
  if (active === id) {
    return true;
  }
  const target = findThread(state, id);
  return target !== undefined && findThread(state, active) === target;
}

export interface Thread {
  id: string;
  title: string | null;
  /**
   * loadStatus defines whether the thread is a 'summary' (metadata-only) or
   * fully-hydrated with a message tree.
   */
  loadStatus: 'summary' | 'loaded';
  /**
   * promptSnippet is the server-derived first-prompt excerpt mirrored on the
   * client. Used as a display fallback when `title` is empty.
   */
  promptSnippet: string | null;
  status: 'idle' | 'running' | 'error';
  error: string | null;
  rootTurnId: string | null;
  latestTurnId: string | null;
  messageTree: MessageTree;
  currentRunId: string | null;
  currentMessageId: string | null;
  runOutcomes: Record<string, RunOutcome>;
  createdAt: number;
  /** Last server-confirmed metadata update, such as rename/title changes. */
  updatedAt: number;
  /** Last user or agent message activity in the conversation. */
  lastActivityAt: number;
  /** Projects referenced by the thread's prompts; used for chip display. */
  projects: Array<{entityName: string; projectName: string}>;
  /**
   * pendingQuestions holds structured questions the last completed turn asked.
   * When set, the composer presents them as a guided dialog for the next
   * prompt. Cleared when a new run starts.
   */
  pendingQuestions: AgentQuestion[] | null;
  /**
   * pendingNetworkAccessRequest holds the elevated-network-access request the
   * last completed turn made. When set, the composer presents a grant/deny
   * element. Cleared when a new run starts.
   */
  pendingNetworkAccessRequest: NetworkAccessRequest | null;
  /**
   * activeNetworkDomains is the network allowlist the most recent turn ran with
   * (its resolved `permissions.allowed_network_domains`). Derived from turn
   * data, not accumulated client-side: the composer offers these as removable
   * chips, and grants/revokes send a new allowlist on the next turn's patch.
   */
  activeNetworkDomains: string[];
}

// MAX_THREAD_TITLE_LENGTH defines the maximum supported title length, per the
// wb_agent contract: VARCHAR(255) + Pydantic max_length=255, counted in code
// points so emoji/non-Latin titles aren't over-limited.
export const MAX_THREAD_TITLE_LENGTH = 255;

/**
 * clampToTitleLength truncates to MAX_THREAD_TITLE_LENGTH counted in code
 * points (not UTF-16 units), so emoji and non-Latin titles aren't cut earlier
 * than the server allows. Safe to call on every keystroke.
 */
export function clampToTitleLength(s: string): string {
  const points = [...s];
  return points.length > MAX_THREAD_TITLE_LENGTH
    ? points.slice(0, MAX_THREAD_TITLE_LENGTH).join('')
    : s;
}

/**
 * sanitizeThreadTitle normalizes a user-entered title to the single-line form
 * the server stores. Preserves emoji ZWJ sequences, combining marks, bidi
 * controls, and non-Latin scripts; removes only control characters that render
 * as broken glyphs, then clamps to the server's length in code points.
 */
export function sanitizeThreadTitle(raw: string): string {
  const cleaned = raw
    .normalize('NFC')
    // Drop control chars, but fold whitespace controls (tab, newline) into a
    // space so words keep a gap; the collapse below then dedupes runs.
    .replace(/\p{Cc}/gu, c => (/\s/.test(c) ? ' ' : ''))
    .replace(/\s+/g, ' ')
    .trim();

  return clampToTitleLength(cleaned);
}

export interface MessageTree {
  /** activeChildId maps parent IDs to their active child ID. */
  activeChildId: Record<string, string>;
  /** children maps parent IDs to their ordered child IDs. */
  children: Record<string, string[]>;
  /** currentNodeId is the latest in the active branch. */
  currentNodeId: string | null;
  /** nodes maps IDs to the message nodes. */
  nodes: Record<string, MessageNode>;
  /** parentId maps node IDs to their parent IDs (null for root-level nodes). */
  parentId: Record<string, string | null>;
  /** rootChildren lists all top-level node IDs. */
  rootChildren: string[];
}

export interface MessageNode {
  id: string;
  role: MessageRole;
  content: ContentPart[];
  createdAt: number;
}

export type ContentPart =
  | TextPart
  | ThinkingPart
  | ToolCallPart
  | ImagePart
  | WandbEntityPart
  | WandbProjectPart
  | WandbRunPart
  | WandbCallPart
  | WandbWebPageStatePart
  | UnknownPart;

export interface TextPart {
  type: 'text';
  text: string;
  status: 'in-progress' | 'complete';
}

export interface ThinkingPart {
  type: 'thinking';
  text: string;
  status: 'in-progress' | 'complete';
}

export interface ToolCallPart {
  type: 'tool-call';
  toolCallId: string;
  toolName: string;
  argsText: string;
  args: Record<string, ReadonlyJSONValue> | null;
  status: 'pending' | 'executing' | 'done' | 'error';
  result?: string;
  isError?: boolean;
}

export interface ImagePart {
  type: 'image';
  mediaType: ImageMediaType;
  base64Data: string;
}
export interface WandbEntityPart {
  type: 'wandb-entity';
  entityName: string;
}
export interface WandbProjectPart {
  type: 'wandb-project';
  entityName: string;
  projectName: string;
}

export type RunDisplayNameStatus =
  | 'pending'
  | 'resolved'
  | 'not-found'
  | 'error';
export interface WandbRunPart {
  type: 'wandb-run';
  entityName: string;
  projectName: string;
  runName: string;
  runDisplayName?: string;
  runDisplayNameStatus?: RunDisplayNameStatus;
}

export interface WandbCallPart {
  type: 'wandb-call';
  entityName: string;
  projectName: string;
  callId: string;
}

export interface WandbWebPageStatePart {
  type: 'wandb-web-page-state';
  url: string;
  entityName?: string;
  projectName?: string;
  content?: PagePromptContent[];
}

/**
 * PagePromptContent is a discriminated union of page content; grows as new page
 * kinds get added.
 */
export type PagePromptContent =
  | WorkspacePageContent
  | ReportPageContent
  | SweepPageContent;

export interface WorkspacePageContent {
  type: 'workspace';
  viewId?: string;
  viewName?: string;
  viewType?: 'project-view' | 'sweep-view' | 'group-view';
  viewScope?: 'personal' | 'shared';
  runCountTotal?: number;
  runCountFiltered?: number;
  runCountListed?: number;
  runFiltersExpression?: string;
  panelSearchQuery?: string;
  runSelectorGrouping?: RunSelectorGroupingKey[];
}

export interface ReportPageContent {
  type: 'report';
  title?: string;
  description?: string;
  runSets?: ReportRunSet[];
}

/**
 * ReportRunSet is a single run set within a report, as collected from its run
 * selector. A report's run sets can target arbitrary projects, so each carries
 * its own entity and project.
 */
export interface ReportRunSet {
  name?: string;
  entityName?: string;
  projectName?: string;
  runCountFiltered?: number;
  runFiltersExpression?: string;
  enabled?: boolean;
}

export interface SweepMetric {
  name: string;
  goal?: 'minimize' | 'maximize';
}

/**
 * SweepPageContent frames how to read a sweep's runs via its state, search
 * method, and optimization metric.
 *
 * NOTE: Filters are placed on the 'workspace' layer the 'sweep' stacks over.
 */
export interface SweepPageContent {
  type: 'sweep';
  state?:
    | 'pending'
    | 'running'
    | 'paused'
    | 'finished'
    | 'canceled'
    | 'flapping'
    | 'unknown';
  method?: 'bayes' | 'grid' | 'random' | 'custom';
  metric?: SweepMetric;
  /**
   * runCount is the total runs the sweep has spawned, distinct from the
   * filtered count on the workspace layer it stacks over.
   */
  runCount?: number;
  /** runCountExpected is the sweep's configured run target, when one exists. */
  runCountExpected?: number;
}

export interface RunSelectorGroupingKey {
  section: string;
  name: string;
}

/** Fallback for forward-compatibility with future BE part types. */
export interface UnknownPart {
  type: 'unknown';
  rawType: string;
  data: ReadonlyJSONValue;
}

export function createDefaultState(): AgentChatState {
  return {
    threads: {},
    drafts: {},
    promotionAliases: {},
    activeThreadId: null,
    isActiveThreadLoading: false,
    activeThreadLoadFailure: null,
    pendingRenames: {},
    pendingDeletes: new Set(),
    deletedThreadIds: new Set(),
  };
}

export function createEmptyThread(id: string, now = Date.now()): Thread {
  return {
    id,
    title: null,
    status: 'idle',
    error: null,
    rootTurnId: null,
    latestTurnId: null,
    messageTree: createEmptyTree(),
    currentRunId: null,
    currentMessageId: null,
    runOutcomes: {},
    createdAt: now,
    updatedAt: now,
    lastActivityAt: now,
    loadStatus: 'loaded',
    promptSnippet: null,
    projects: [],
    pendingQuestions: null,
    pendingNetworkAccessRequest: null,
    activeNetworkDomains: [],
  };
}

// ThreadSummary is a backend-derived summary from a "list turns" call.
export type ThreadSummary = Pick<
  Thread,
  | 'id'
  | 'title'
  | 'updatedAt'
  | 'lastActivityAt'
  | 'createdAt'
  | 'promptSnippet'
  | 'projects'
> & {
  rootTurnId: string; // Backend-derived, so `rootTurnId` definitely exists.
};

export function createThreadFromSummary(args: ThreadSummary): Thread {
  return {
    id: args.id,
    title: args.title,
    status: 'idle',
    error: null,
    rootTurnId: args.rootTurnId,
    latestTurnId: args.rootTurnId,
    messageTree: createEmptyTree(),
    currentRunId: null,
    currentMessageId: null,
    runOutcomes: {},
    createdAt: args.createdAt,
    updatedAt: args.updatedAt,
    lastActivityAt: args.lastActivityAt,
    loadStatus: 'summary',
    promptSnippet: args.promptSnippet,
    projects: args.projects,
    pendingQuestions: null,
    pendingNetworkAccessRequest: null,
    activeNetworkDomains: [],
  };
}

export function createEmptyTree(): MessageTree {
  return {
    nodes: {},
    parentId: {},
    children: {},
    rootChildren: [],
    currentNodeId: null,
    activeChildId: {},
  };
}

/**
 * Returns true if `parts` contains anything worth sending as a user prompt.
 * Whitespace-only text and empty-text delimiters are treated as trivial; any
 * non-text part (mention, image, etc.) is considered meaningful on its own.
 */
export function hasMeaningfulContent(parts: readonly ContentPart[]): boolean {
  return parts.some(p => p.type !== 'text' || p.text.trim().length > 0);
}
